import { Box, Container, Typography, useTheme } from "@mui/material";
import { NavigationAndFooterLayout } from "../layouts";
import { NextPageWithLayout } from "@cm-websites/common";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import Image from "next/image";
import NextLink from "next/link";
import React from "react";
import TatortEsstisch from "../../public/images/tatort-esstisch.jpg";
import type { GetStaticProps } from "next";

export const WasIstDasPage: NextPageWithLayout = () => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <Container maxWidth="md" sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: 4 }}>
        <Typography variant="h1" sx={{ pb: 6, hyphens: "none" }}>
          What is a murder mystery game?
        </Typography>
        <Typography variant="body1" paragraph>
          You and your guests slip into the roles of the main suspects of an exciting murder case. The
          objective of the game is to convict the perpetrator – one of the people sitting with you at the
          table. This can only be achieved if the players discuss the course of events amongst themselves
          and compare their experiences of what happened. But be careful: Each character (including you!)
          has their own dark secrets which they’d be better off keeping to themselves.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 0, pb: 0 }}>
        <Box>
          <Image
            alt="Murder Mystery Party Game"
            placeholder="blur"
            src={TatortEsstisch}
            title="Invite your friends to a murder mystery party!"
            style={{
              borderRadius: theme.shape.borderRadius,
              width: "100%",
              height: "auto",
              overflow: "hidden",
            }}
          />
        </Box>
      </Container>
      <Container maxWidth="md" sx={{ pt: 4, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}>
        <Typography variant="body1" paragraph>
          You all gather in the wake of the crime and attempt to piece together your memories, with the
          aim of identifying the perpetrator. In each round, you recall more and more about the
          circumstances of the crime, as each round deals with a different period of time from the
          evening the crime was committed. At the end, everyone votes on who should be handed over to the
          police as the suspect.
        </Typography>

        <Typography variant="body1" paragraph>
          Culinario Mortale is a highly interactive game. There are only a few rules, and no pre-prepared
          dialogue. The focus is on working through the puzzles and discussing the events together.
        </Typography>

        <Typography variant="body1" paragraph>
          Alongside a well-structured game manual, each game contains game booklets for between five and
          eight characters{" "}
          <em>
            (the number of players for each game can be found on the packaging and here on our website)
          </em>
          . In the game booklets, each guest will find their personal clues and a description of their
          role. Each game booklet also includes a brief{" "}
          <NextLink href="/spielregeln-und-ablauf">overview</NextLink> of how the game works. As such,
          all players can jump right in. The support code included with your Culinario Mortale game will
          allow you to <NextLink href="/preparation">download</NextLink> the role descriptions in advance
          from our website in PDF format, and easily send them to your guests via email.
        </Typography>

        <Typography variant="body1" paragraph>
          Should you have any questions, please do not hesitate to contact us. We are always happy to
          receive praise, feedback and suggestions. Send your feedback or question by email to:{" "}
          <NextLink href="mailto:support@culinario-mortale.com">
            support @ culinario-mortale.com
          </NextLink>
        </Typography>
      </Container>
    </React.Fragment>
  );
};

WasIstDasPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default WasIstDasPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale
        ? await serverSideTranslations(locale, ["c-common", "c-user-consent-management"], i18nConfig)
        : {}),
    },
  };
};
